import React, { useState, useRef, Fragment } from 'react';
import Button from '@mui/material/Button';
import ModalAddFuelTransaction from './ModalAddFuelTransaction';
import ModalEditTransaction from './ModalEditTransaction';
import client from '../../../client';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import Box from '@mui/material/Box';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import EditIcon from '@mui/icons-material/Edit';
import WarningIcon from '@mui/icons-material/Warning';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import Switch from '@mui/material/Switch';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import Collapse from '@mui/material/Collapse';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableContainer from '@mui/material/TableContainer';
import TableFooter from '@mui/material/TableFooter';
import TablePagination from '@mui/material/TablePagination';
import TableSortLabel from '@mui/material/TableSortLabel';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Tooltip from '@mui/material/Tooltip';
import { visuallyHidden } from '@mui/utils';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import FirstPageIcon from '@mui/icons-material/FirstPage';
import KeyboardArrowLeft from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRight from '@mui/icons-material/KeyboardArrowRight';
import LastPageIcon from '@mui/icons-material/LastPage';
import { useTheme } from '@mui/material/styles';
import { useMessage } from 'app/shared-components/MessageUtils';
import { useLocation } from 'react-router-dom';
import { formatNumber, threeDecimalformatCurrency } from '../../../utils/numberFormatting';
import { useTranslation } from 'react-i18next';
import FuseLoading from '@fuse/core/FuseLoading';
import PropTypes from 'prop-types';

// Table Pagination Actions Component
function TablePaginationActions(props) {
  const theme = useTheme();
  const { count, page, rowsPerPage, onPageChange } = props;

  const handleFirstPageButtonClick = (event) => {
    onPageChange(event, 0);
  };

  const handleBackButtonClick = (event) => {
    onPageChange(event, page - 1);
  };

  const handleNextButtonClick = (event) => {
    onPageChange(event, page + 1);
  };

  const handleLastPageButtonClick = (event) => {
    onPageChange(event, Math.max(0, Math.ceil(count / rowsPerPage) - 1));
  };

  return (
    <Box sx={{ flexShrink: 0, ml: 2.5 }}>
      <IconButton
        onClick={handleFirstPageButtonClick}
        disabled={page === 0}
        aria-label='first page'
      >
        {theme.direction === 'rtl' ? <LastPageIcon /> : <FirstPageIcon />}
      </IconButton>
      <IconButton onClick={handleBackButtonClick} disabled={page === 0} aria-label='previous page'>
        {theme.direction === 'rtl' ? <KeyboardArrowRight /> : <KeyboardArrowLeft />}
      </IconButton>
      <IconButton
        onClick={handleNextButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label='next page'
      >
        {theme.direction === 'rtl' ? <KeyboardArrowLeft /> : <KeyboardArrowRight />}
      </IconButton>
      <IconButton
        onClick={handleLastPageButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label='last page'
      >
        {theme.direction === 'rtl' ? <FirstPageIcon /> : <LastPageIcon />}
      </IconButton>
    </Box>
  );
}

TablePaginationActions.propTypes = {
  count: PropTypes.number.isRequired,
  onPageChange: PropTypes.func.isRequired,
  page: PropTypes.number.isRequired,
  rowsPerPage: PropTypes.number.isRequired,
};

// Sorting helper functions
function descendingComparator(a, b, orderBy) {
  // Handle numeric columns (may be stored as strings)
  const numericColumns = ['quantity', 'price_per_unit', 'value', 'mileage'];
  if (numericColumns.includes(orderBy)) {
    const aNum = parseFloat(a[orderBy]) || 0;
    const bNum = parseFloat(b[orderBy]) || 0;
    if (bNum < aNum) {
      return -1;
    }
    if (bNum > aNum) {
      return 1;
    }
    return 0;
  }
  
  // Handle date strings
  if (orderBy === 'date_created') {
    const dateA = new Date(a[orderBy]);
    const dateB = new Date(b[orderBy]);
    if (isNaN(dateA.getTime()) || isNaN(dateB.getTime())) {
      // If dates are invalid, treat as strings
      const aValue = a[orderBy]?.toString().toLowerCase() || '';
      const bValue = b[orderBy]?.toString().toLowerCase() || '';
      if (bValue < aValue) return -1;
      if (bValue > aValue) return 1;
      return 0;
    }
    if (dateB < dateA) {
      return -1;
    }
    if (dateB > dateA) {
      return 1;
    }
    return 0;
  }
  
  // Handle numeric values (direct)
  if (typeof a[orderBy] === 'number' && typeof b[orderBy] === 'number') {
    if (b[orderBy] < a[orderBy]) {
      return -1;
    }
    if (b[orderBy] > a[orderBy]) {
      return 1;
    }
    return 0;
  }
  
  // Handle string values
  const aValue = a[orderBy]?.toString().toLowerCase() || '';
  const bValue = b[orderBy]?.toString().toLowerCase() || '';
  if (bValue < aValue) {
    return -1;
  }
  if (bValue > aValue) {
    return 1;
  }
  return 0;
}

function getComparator(order, orderBy) {
  return order === 'desc' 
    ? (a, b) => descendingComparator(a, b, orderBy) 
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function stableSort(array, comparator) {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) {
      return order;
    }
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
}

const Transactions = () => {
  const { t } = useTranslation('navigation');
  // Get compacctnum from localStorage
  let compacctnum = '';
  try {
    const lSData = JSON.parse(localStorage.getItem('persist:root'));
    const userData = JSON.parse(lSData.loadz_auth).AuthReducer.user;
    compacctnum = userData.company.compacctnum;
  } catch (e) {
    compacctnum = '';
  }

  // Get URL parameters
  const location = useLocation();
  const urlParams = new URLSearchParams(location.search);
  const batchIdFromUrl = urlParams.get('batch_id');
  const [loading, setLoading] = React.useState(true);
  const [transactions, setTransactions] = React.useState([]);
  const [fuelTanks, setFuelTanks] = React.useState([]);
  const [driverData, setDriverData] = useState([]);
  const [truckData, setTruckData] = useState([]);
  const [openAddTransaction, setOpenAddTransaction] = useState(false);
  const [openEditTransaction, setOpenEditTransaction] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [selectedBatch, setSelectedBatch] = useState(null);
  const [fuelBatches, setFuelBatches] = useState([]);
  const [selectedBatchData, setSelectedBatchData] = useState(null);
  const [pastBatches, setPastBatches] = useState(false);
  const [filterByBatch, setFilterByBatch] = useState(false);
  const [filterByTruck, setFilterByTruck] = useState(false);
  const [selectedTruck, setSelectedTruck] = useState(null);
  const [filterByDriver, setFilterByDriver] = useState(false);
  const [selectedDrivers, setSelectedDrivers] = useState([]);
  const [fuelnLubeCatalog, setFuelnLubeCatalog] = useState([]);
  const [currentTrucksWhitTransactions, setCurrentTrucksWhitTransactions] = useState([]);
  const [currentDriversWhitTransactions, setCurrentDriversWhitTransactions] = useState([]);
  const [filterByDateRange, setFilterByDateRange] = useState(false);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [adjustmentTransactions, setAdjustmentTransactions] = useState([]);
  
  // State for delete adjustment modal
  const [openDeleteAdjustmentModal, setOpenDeleteAdjustmentModal] = useState(false);
  const [deleteAdjustmentResponse, setDeleteAdjustmentResponse] = useState(null);
  const [selectedAdjustmentTransaction, setSelectedAdjustmentTransaction] = useState(null);
  const [confirmDeleteCheckbox, setConfirmDeleteCheckbox] = useState(false);
  const [confirmInventoryReductionCheckbox, setConfirmInventoryReductionCheckbox] = useState(false);
  
  // Unified filter state
  const [filtersApplied, setFiltersApplied] = useState(false);
  
  // State for expanded rows - store the row ID that is currently expanded
  const [expandedRowId, setExpandedRowId] = useState(null);
  
  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  
  // Sorting state
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState(null);

  // Refs to track previous filter values to detect when filters are cleared
  const prevFilterByBatch = useRef(filterByBatch);
  const prevFilterByTruck = useRef(filterByTruck);
  const prevFilterByDriver = useRef(filterByDriver);
  const prevFilterByDateRange = useRef(filterByDateRange);
  const prevSelectedBatch = useRef(selectedBatch);
  const prevSelectedTruck = useRef(selectedTruck);
  const prevSelectedDrivers = useRef(selectedDrivers);
  const prevStartDate = useRef(startDate);
  const prevEndDate = useRef(endDate);

  // Hook to show messages
  const showMessage = useMessage();

  // Validations for fuelTanks and fuelBatches
  const validateFuelTanks = () => {
    if (!fuelTanks || fuelTanks.length === 0) {
      showMessage(t('NO_FUEL_TANKS_DATA'), 'warning');
      return false;
    }
    
    return true;
  };

  const validateFuelBatches = () => {
    if (!fuelBatches || fuelBatches.length === 0) {
      showMessage(t('NO_FUEL_BATCHES_DATA'), 'warning');
      return false;
    }
    
    return true;
  };

  const getFullData = async () => {
      setLoading(true);
      try {
        const response = await client.get(`/fuel-inventory/full-transaction-data/${compacctnum}`);
        if (response && response.data) {
          const newFuelTanks = response.data.data.fuelTankData || [];
          const newFuelBatches = response.data.data.fuelBatches || [];
          
          setFuelTanks(newFuelTanks);
          setDriverData(response.data.data.driverData || []);
          setTruckData(response.data.data.trucksData || []);
          setTransactions(response.data.data.transactions || []);
          setFuelBatches(newFuelBatches);
          setFuelnLubeCatalog(response.data.data.fuelNLubeCatalogData || []);
          setCurrentTrucksWhitTransactions(response.data.data.currentTrucksWhitTransactions || []);
          setCurrentDriversWhitTransactions(response.data.data.currentDriversWhitTransactions || []);
          setAdjustmentTransactions(response.data.data.adjustmentTransactions || []);
        }
      } catch (e) {
        setFuelTanks([]);
        setDriverData([]);
        setTruckData([]);
        setTransactions([]);
        setFuelBatches([]);
        showMessage(t('FAILED_LOAD_FUEL_INVENTORY'), 'error');
      }
      setLoading(false);
  };
  
  React.useEffect(() => {
      getFullData();
    }, []);

  // Handle URL parameter for batch filtering
  React.useEffect(() => {
    if (batchIdFromUrl && fuelBatches.length > 0 && !filterByBatch) {
      const batchId = batchIdFromUrl;
      
      // Try different comparison methods to find the batch
      let selectedBatchData = fuelBatches.find(t => t.batch_id === batchId);
      if (!selectedBatchData) {
        // Try comparing as strings
        selectedBatchData = fuelBatches.find(t => String(t.batch_id) === String(batchId));
      }
      if (!selectedBatchData) {
        // Try comparing the original string from URL
        selectedBatchData = fuelBatches.find(t => String(t.batch_id) === batchIdFromUrl);
      }
      
      if (selectedBatchData) {
        setSelectedBatch(batchId);
        setFilterByBatch(true);
        setSelectedBatchData(selectedBatchData);
        showMessage(`${t('FILTERING_TRANSACTIONS_BATCH')}${batchId}`, 'info');
      } else {
        showMessage(`${t('BATCH_NOT_FOUND')}${batchId} ${fuelBatches.map(t => t.batch_id).join(', ')}`, 'warning');
      }
    }
  }, [batchIdFromUrl, fuelBatches, filterByBatch]);

  // Note: Batch filtering is now handled by the unified applyAllFilters function
  // This useEffect has been removed to prevent interference with the filter options

  // Monitor filter changes and automatically apply filters ONLY when a filter is cleared
  React.useEffect(() => {
    let shouldApplyFilters = false;

    // Check if batch filter was cleared
    if (prevFilterByBatch.current && !filterByBatch) {
      shouldApplyFilters = true;
    }
    
    // Check if truck filter was cleared
    if (prevFilterByTruck.current && !filterByTruck) {
      shouldApplyFilters = true;
    }
    
    // Check if driver filter was cleared
    if (prevFilterByDriver.current && !filterByDriver) {
      shouldApplyFilters = true;
    }
    
    // Check if date range filter was cleared
    if (prevFilterByDateRange.current && !filterByDateRange) {
      shouldApplyFilters = true;
    }

    // Check if specific filter values were cleared (null/empty)
    if (prevSelectedBatch.current && !selectedBatch) {
      shouldApplyFilters = true;
    }
    
    if (prevSelectedTruck.current && !selectedTruck) {
      shouldApplyFilters = true;
    }
    
    if (prevSelectedDrivers.current && prevSelectedDrivers.current.length > 0 && (!selectedDrivers || selectedDrivers.length === 0)) {
      shouldApplyFilters = true;
    }
    
    if (prevStartDate.current && !startDate) {
      shouldApplyFilters = true;
    }
    
    if (prevEndDate.current && !endDate) {
      shouldApplyFilters = true;
    }

    // Apply filters only if a filter was cleared
    if (shouldApplyFilters) {
      applyAllFilters();
    }

    // Update refs with current values
    prevFilterByBatch.current = filterByBatch;
    prevFilterByTruck.current = filterByTruck;
    prevFilterByDriver.current = filterByDriver;
    prevFilterByDateRange.current = filterByDateRange;
    prevSelectedBatch.current = selectedBatch;
    prevSelectedTruck.current = selectedTruck;
    prevSelectedDrivers.current = selectedDrivers;
    prevStartDate.current = startDate;
    prevEndDate.current = endDate;
  }, [filterByBatch, filterByTruck, filterByDriver, filterByDateRange, selectedBatch, selectedTruck, selectedDrivers, startDate, endDate]);

  // Validate data when fuelTanks or fuelBatches change
  React.useEffect(() => {
    if (fuelTanks.length > 0 || fuelBatches.length > 0) {
      // Only validate if there is loaded data
      if (fuelTanks.length > 0) {
        validateFuelTanks();
      }
      if (fuelBatches.length > 0) {
        validateFuelBatches();
      }
    }
  }, [fuelTanks, fuelBatches]);

  // Helper function to format date as yyyy/mm/dd HH:mm
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    if (isNaN(date)) return '';
    const yyyy = date.getFullYear();
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const dd = String(date.getDate()).padStart(2, '0');
    const HH = String(date.getHours()).padStart(2, '0');
    const min = String(date.getMinutes()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd} ${HH}:${min}`;
  };

  async function GetPastBatchesData(){
    setLoading(true);
      try {
        const response = await client.get(`/fuel-inventory/past-batches-data/${compacctnum}`);
        if (response && response.data) {
          setFuelBatches(response.data.data.fuelBatches || []);
          setCurrentTrucksWhitTransactions(response.data.data.pastTrucksData || []);
          setCurrentDriversWhitTransactions(response.data.data.pastDriversData || []);
          setTransactions(response.data.data.fuelTransactions || []);
          setAdjustmentTransactions(response.data.data.adjustmentTransactions || []);

      }
      } catch (e) {
        setFuelBatches([]);
        showMessage(t('FAILED_LOAD_PAST_BATCHES'), 'error');
      }
      setLoading(false);
  }

  async function GetFilteredTransactionsByDrivers(selectedDriverNames) {
    setLoading(true);
    try {
      const driverIds = selectedDriverNames.filter(driver => driver.dr_user_id).map(driver => driver.dr_user_id);
      const requestData = {
        drivers: driverIds
      };
      
      if(startDate){
        requestData.start_date = startDate.toISOString().split('T')[0];
      }
      if(endDate){
        requestData.end_date = endDate.toISOString().split('T')[0];
      }
      
      const response = await client.post(`/fuel-inventory/transactions-by-drivers/${compacctnum}`, requestData);
      if (response && response.data) {
        setTransactions(response.data.data.transactions || []);
        showMessage(`${response.data.data.transactions?.length || 0} ${t('FOUND_TRANSACTIONS_DRIVERS')}`, 'info');
      }
    } catch (e) {
      setTransactions([]);
      showMessage(t('FAILED_LOAD_FILTERED_TRANSACTIONS_DRIVERS'), 'error');
    }
    setLoading(false);
  }

  async function GetFilteredTransactionsByDateRange(startDate, endDate) {
    setLoading(true);
    try {
      const response = await client.post(`/fuel-inventory/transactions-by-date-range/${compacctnum}`, {
        start_date: startDate.toISOString().split('T')[0],
        end_date: endDate.toISOString().split('T')[0]
      });
      if (response && response.data) {
        setTransactions(response.data.data.transactions || []);
        showMessage(`${response.data.data.transactions?.length || 0} ${t('FOUND_TRANSACTIONS_DATE_RANGE')}`, 'info');
      }
    } catch (e) {
      setTransactions([]);
      showMessage(t('FAILED_LOAD_FILTERED_TRANSACTIONS_DATE'), 'error');
    }
    setLoading(false);
  }

  async function GetFilteredTransactionsByTruck(selectedTruck) {
    setLoading(true);
    try {
      const requestData = {
        truck_id: selectedTruck.idx_trucks_p
      };
      
      if(startDate){
        requestData.start_date = startDate.toISOString().split('T')[0];
      }
      if(endDate){
        requestData.end_date = endDate.toISOString().split('T')[0];
      }
      
      const response = await client.post(`/fuel-inventory/transactions-by-truck/${compacctnum}`, requestData);
      if (response && response.data) {
        setTransactions(response.data.data.transactions || []);
        showMessage(`${response.data.data.transactions?.length || 0} ${t('FOUND_TRANSACTIONS_TRUCK')}${selectedTruck.t_no_truck}`, 'info');
      }
    } catch (e) {
      setTransactions([]);
      showMessage(t('FAILED_LOAD_FILTERED_TRANSACTIONS_TRUCK'), 'error');
    }
    setLoading(false);
  }

  // Unified function to apply all filters with a single API call
  async function applyAllFilters() {
    // Validate date range if both dates are provided
    if (startDate && endDate && startDate > endDate) {
      showMessage(t('START_DATE_BEFORE_END_DATE'), 'warning');
      return;
    }

    // If no filters are selected, load all data
    if (!filterByBatch && !filterByTruck && !filterByDriver && !filterByDateRange) {
      if (pastBatches) {
        GetPastBatchesData();
      } else {
        getFullData();
      }
      setFiltersApplied(false);
      return;
    }

    // Validate specific filter requirements
    if (filterByDriver && selectedDrivers.length === 0) {
      showMessage(t('SELECT_AT_LEAST_ONE_DRIVER'), 'warning');
      return;
    }

    if (filterByDateRange && (!startDate || !endDate)) {
      showMessage(t('SELECT_BOTH_DATES'), 'warning');
      return;
    }

    setLoading(true);
    try {
      // Build request data with all active filters
      const requestData = {};

      // Add batch filter
      if (filterByBatch && selectedBatch) {
        requestData.batch_id = selectedBatch;
      }

      // Add truck filter
      if (filterByTruck && selectedTruck) {
        requestData.truck_id = selectedTruck.idx_trucks_p;
      }

      // Add drivers filter
      if (filterByDriver && selectedDrivers.length > 0) {
        const driverIds = selectedDrivers.filter(driver => driver.dr_user_id).map(driver => driver.dr_user_id);
        requestData.drivers = driverIds;
      }

      // Add date range filter
      if (startDate) {
        requestData.start_date = startDate.toISOString().split('T')[0];
      }
      if (endDate) {
        requestData.end_date = endDate.toISOString().split('T')[0];
      }

      // Add batch type (active/past)
      requestData.past_batches = pastBatches;

      // Single API call with all filters
      const response = await client.post(`/fuel-inventory/full-filtered-transactions/${compacctnum}`, requestData);

      if (response && response.data) {
        const newFuelTanks = response.data.data.fuelTankData || [];
        const newFuelBatches = response.data.data.fuelBatches || [];
        
        setFuelTanks(newFuelTanks);
        setDriverData(response.data.data.driverData || []);
        setTruckData(response.data.data.trucksData || []);
        setTransactions(response.data.data.transactions || []);
        setFuelBatches(newFuelBatches);
        setFuelnLubeCatalog(response.data.data.fuelNLubeCatalogData || []);
        setCurrentTrucksWhitTransactions(response.data.data.currentTrucksWhitTransactions || []);
        setCurrentDriversWhitTransactions(response.data.data.currentDriversWhitTransactions || []);
        setFiltersApplied(true);
        
        const transactionCount = response.data.data.transactions?.length || 0;
        showMessage(`${transactionCount} ${t('FOUND_TRANSACTIONS_WITH_FILTERS')}`, 'info');
      }
    } catch (e) {
      setFuelTanks([]);
      setDriverData([]);
      setTruckData([]);
      setTransactions([]);
      setFuelBatches([]);
      setFuelnLubeCatalog([]);
      setCurrentTrucksWhitTransactions([]);
      setCurrentDriversWhitTransactions([]);
      showMessage(t('FAILED_LOAD_FILTERED_TRANSACTIONS'), 'error');
    }
    setLoading(false);
  }

  // Function to clear all filters
  function clearAllFilters() {
    setSelectedBatch(null);
    setSelectedBatchData(null);
    setSelectedTruck(null);
    setSelectedDrivers([]);
    setStartDate(null);
    setEndDate(null);
    setFilterByBatch(false);
    setFilterByTruck(false);
    setFilterByDriver(false);
    setFilterByDateRange(false);
    setFiltersApplied(false);
    
    // Reload data based on current batch view
    if (pastBatches) {
      GetPastBatchesData();
    } else {
      getFullData();
    }
    
    showMessage(t('ALL_FILTERS_CLEARED'), 'info');
  }

  function BatchesSelector(selector){
    if(selector){
      //here we will put the data of the currently active batches
      console.log('active');
      getFullData();
    }else{
      //here we will put the data of the past batches
      console.log('past');
      GetPastBatchesData();
    }
  }

  // Validate before opening the transaction modal
  const handleOpenAddTransaction = () => {
    if (!validateFuelTanks()) {
      showMessage(t('CANNOT_ADD_TRANSACTION_TANKS'), 'error');
      return;
    }
    
    if (!validateFuelBatches()) {
      showMessage(t('CANNOT_ADD_TRANSACTION_BATCHES'), 'error');
      return;
    }
    
    setOpenAddTransaction(true);
  };

  // Handle opening edit transaction modal
  const handleOpenEditTransaction = (transaction) => {
    setSelectedTransaction(transaction);
    setOpenEditTransaction(true);
  };

  // Handle updating transaction - simplified callback to refresh data
  const handleUpdateTransaction = () => {
    // Reload data to reflect changes
    if (filtersApplied) {
      applyAllFilters();
    } else if (pastBatches) {
      GetPastBatchesData();
    } else {
      getFullData();
    }
  };

  // Handle deleting adjustment transaction
  const handleDeleteAdjustment = async (transaction) => {
    // Store transaction data for later use
    setSelectedAdjustmentTransaction(transaction);
    
    try {
      const response = await client.delete(`/fuel-inventory/validate-delete-adjustment-transaction/${compacctnum}`, {
        data: {
          transaction_id: transaction.transaction_id,
          adjustment_group: transaction.adjustment_group,
          adjustment_type: transaction.adjustment_type,
          batch_id: transaction.batch_id,
          adjustment_id: transaction.id,
          transaction_id: transaction.transaction_id,
          parent_transaction_id: transaction.parent_transaction_id,
        }
      });
      
      // Store response and open modal
      if (response && response.data) {
        setDeleteAdjustmentResponse(response.data);
        setOpenDeleteAdjustmentModal(true);
      }
    } catch (e) {
      // Store error response and open modal
      const errorData = e.response?.data || { 
        message: e.response?.data?.message || e.response?.data?.msg || e.response?.data?.error || (t('FAILED_DELETE_ADJUSTMENT') || 'Failed to delete adjustment')
      };
      setDeleteAdjustmentResponse(errorData);
      setOpenDeleteAdjustmentModal(true);
    }
  };

  // Handle closing delete adjustment modal (Cancel button)
  const handleCloseDeleteAdjustmentModal = () => {
    setOpenDeleteAdjustmentModal(false);
    setDeleteAdjustmentResponse(null);
    setSelectedAdjustmentTransaction(null);
    setConfirmDeleteCheckbox(false);
    setConfirmInventoryReductionCheckbox(false);
  };

  // Handle proceed with deletion (Proceed button)
  const handleProceedDeleteAdjustment = async () => {
    if (!selectedAdjustmentTransaction) return;

    try {
      const response = await client.delete(`/fuel-inventory/delete-adjustment-transaction/${compacctnum}`, {
        data: {
          transaction_id: selectedAdjustmentTransaction.transaction_id,
          adjustment_group: selectedAdjustmentTransaction.adjustment_group,
          adjustment_type: selectedAdjustmentTransaction.adjustment_type,
          batch_id: selectedAdjustmentTransaction.batch_id,
          adjustment_id: selectedAdjustmentTransaction.id,
          parent_transaction_id: selectedAdjustmentTransaction.parent_transaction_id,
        }
      });
      
      if (response && response.data) {
        const backendMessage = response.data.message || response.data.msg || response.data.error;
        if (backendMessage) {
          showMessage(backendMessage, 'success');
        } else {
          showMessage(t('ADJUSTMENT_DELETED_SUCCESSFULLY') || 'Adjustment deleted successfully', 'success');
        }
        
        // Close modal and reset state
        setOpenDeleteAdjustmentModal(false);
        setDeleteAdjustmentResponse(null);
        setSelectedAdjustmentTransaction(null);
        setConfirmDeleteCheckbox(false);
        setConfirmInventoryReductionCheckbox(false);
        
        // Reload data to reflect changes
        if (filtersApplied) {
          applyAllFilters();
        } else if (pastBatches) {
          GetPastBatchesData();
        } else {
          getFullData();
        }
      }
    } catch (e) {
      const errorMessage = e.response?.data?.message || e.response?.data?.msg || e.response?.data?.error;
      if (errorMessage) {
        showMessage(errorMessage, 'error');
      } else {
        showMessage(t('FAILED_DELETE_ADJUSTMENT') || 'Failed to delete adjustment', 'error');
      }
      
      // Close modal and reset state even on error
      setOpenDeleteAdjustmentModal(false);
      setDeleteAdjustmentResponse(null);
      setSelectedAdjustmentTransaction(null);
      setConfirmDeleteCheckbox(false);
      setConfirmInventoryReductionCheckbox(false);
    }
  };


  // Get transactions for a specific batch_id
  const getTransactionsByBatchId = (batchId) => {
    return transactions.filter(t => String(t.batch_id) === String(batchId));
  };

  // Get the currently expanded row data
  const getExpandedRowData = () => {
    if (!expandedRowId) return null;
    return transactions.find(t => t.dbId === expandedRowId);
  };

  // Handle pagination
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Handle sorting
  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
    setPage(0); // Reset to first page when sorting changes
  };

  // Row Component for expandable rows
  function TransactionRow({ row }) {
    const isExpanded = expandedRowId === row.dbId;
    let adjustedQuantity = 0;
    let numberOfAdjustments = 0;
    let subTableRowId = 1;

    const handleToggle = () => {
      if (isExpanded) {
        setExpandedRowId(null);
      } else {
        setExpandedRowId(row.dbId);
      }
    };

    const batchTransactions = getTransactionsByBatchId(row.batch_id);

    return (
      <Fragment>
        <TableRow 
          sx={{ '& > *': { borderBottom: 'unset' }, cursor: 'pointer' }}
          onClick={handleToggle}
        >
          <TableCell>
            { adjustmentTransactions.filter(t => t.new_batch_id === row.batch_id && t.transaction_id === row.transaction_id).length > 0 && (
              <Tooltip title="This transaction has an adjustment, please click to review the information, adjusted transactions are not editable.">
                <WarningIcon sx={{ color: '#ffc107' }} />
              </Tooltip>
            )}
            { row.driver === 'Adjustment Transaction' && row.mileage === 'Adjustment Transaction' && row.notes === 'Adjustment Transaction' && row.truck === 'Adjustment Transaction' && (
              <Tooltip title="This transaction is an adjustment transaction, please click on the edit icon to review the information, adjustment transactions are not editable.">
                <WarningIcon sx={{ color: '#ffc107' }} />
              </Tooltip>
            )}
          </TableCell>
            <TableCell component='th' scope='row'>
              {row.batch_id}
            </TableCell>
          <TableCell>
              <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                <span style={{ flex: 1 }}>{row.transaction_id}</span>
                { adjustmentTransactions.filter(t => t.transaction_id === row.transaction_id).length === 0 ? (
                  <IconButton 
                    size="small" 
                    onClick={(e) => {
                      e.stopPropagation();
                      handleOpenEditTransaction(row);
                    }}
                    sx={{ ml: 1 }}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                ):null}
              </Box>
          </TableCell>
          <TableCell>{formatDate(row.date_created)}</TableCell>
          <TableCell>{formatNumber(row.quantity)} GAL</TableCell>
          <TableCell>${threeDecimalformatCurrency(Number(row.price_per_unit || 0))}</TableCell>
          <TableCell>${threeDecimalformatCurrency(Number(row.value || 0))}</TableCell>
          <TableCell>{row.driver}</TableCell>
          <TableCell>{row.truck}</TableCell>
          <TableCell>{row.mileage}</TableCell>
          <TableCell>{row.tankLocation}</TableCell>
          <TableCell>{row.notes}</TableCell>
        </TableRow>
        <TableRow>
          <TableCell
            style={{ paddingBottom: 0, paddingTop: 0 }}
            colSpan={12}
            className='responsive-loads-table'
          >
            {/* valida que row.batch_id y row.transaction_id este en adjustmentTransactions*/}
            {/* desde este tag 001 */}
            { adjustmentTransactions.filter(t => t.new_batch_id === row.batch_id && t.transaction_id === row.transaction_id).length > 0 && (
              <Collapse in={isExpanded} timeout='auto' unmountOnExit>
                <div style={{ margin: '8px', width: 'auto', overflowX: 'auto' }}>
                  <table style={{ borderCollapse: 'separate', borderSpacing: 0, tableLayout: 'auto', width: '100%', borderRadius: '8px', overflow: 'hidden', border: '1px solid #ccc' }}>
                    <colgroup>
                      <col style={{ width: '1%' }} />
                      <col style={{ width: '1%' }} />
                      <col style={{ width: '1%' }} />
                      <col style={{ width: '1%' }} />
                      <col style={{ width: '1%' }} />
                      <col style={{ width: '1%' }} />
                      <col style={{ width: '1%' }} />
                      <col style={{ width: '1%' }} />
                      <col style={{ width: '1%' }} />
                      <col style={{ width: '1%' }} />
                    </colgroup>
                    <thead>
                      <tr>
                        <th style={{ padding: '4px', border: '1px solid #ccc', fontWeight: 'bold', fontSize: 'inherit', display: 'table-cell', whiteSpace: 'nowrap', backgroundColor: '#edca33' }}>{t('ADJUSTMENT_ID')}</th>
                        <th style={{ padding: '4px', border: '1px solid #ccc', fontWeight: 'bold', fontSize: 'inherit', display: 'table-cell', whiteSpace: 'nowrap', backgroundColor: '#edca33' }}>{t('BATCH_NUMBER')}</th>
                        <th style={{ padding: '4px', border: '1px solid #ccc', fontWeight: 'bold', fontSize: 'inherit', display: 'table-cell', whiteSpace: 'nowrap', backgroundColor: '#edca33' }}>{t('TRANSACTION_ID')}</th>
                        <th style={{ padding: '4px', border: '1px solid #ccc', fontWeight: 'bold', fontSize: 'inherit', display: 'table-cell', whiteSpace: 'nowrap', backgroundColor: '#edca33' }}>{t('ORIGINAL_TRANSACTION_QUANTITY')}</th>
                        <th style={{ padding: '4px', border: '1px solid #ccc', fontWeight: 'bold', fontSize: 'inherit', display: 'table-cell', whiteSpace: 'nowrap', backgroundColor: '#edca33' }}>{t('ADJUSTMENT_QUANTITY')}</th>
                        <th style={{ padding: '4px', border: '1px solid #ccc', fontWeight: 'bold', fontSize: 'inherit', display: 'table-cell', whiteSpace: 'nowrap', backgroundColor: '#edca33' }}>{t('REMAINING_TRANSACTION_QUANTITY')}</th>
                        <th style={{ padding: '4px', border: '1px solid #ccc', fontWeight: 'bold', fontSize: 'inherit', display: 'table-cell', whiteSpace: 'nowrap', backgroundColor: '#edca33' }}>{t('ORIGINAL_TRANSACTION_VALUE')}</th>
                        <th style={{ padding: '4px', border: '1px solid #ccc', fontWeight: 'bold', fontSize: 'inherit', display: 'table-cell', whiteSpace: 'nowrap', backgroundColor: '#edca33' }}>{t('ADJUSTED_TRANSACTION_VALUE')}</th>
                        <th style={{ padding: '4px', border: '1px solid #ccc', fontWeight: 'bold', fontSize: 'inherit', display: 'table-cell', whiteSpace: 'nowrap', backgroundColor: '#edca33' }}>{t('DATE_CREATED')}</th>
                        <th style={{ padding: '4px', border: '1px solid #ccc', fontWeight: 'bold', fontSize: 'inherit', display: 'table-cell', whiteSpace: 'nowrap', backgroundColor: '#edca33' }}>{t('DELETE_ADJUSTMENT')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {adjustmentTransactions.filter(t => t.new_batch_id === row.batch_id && t.transaction_id === row.transaction_id && t.original_quantity === row.quantity && t.trn_rltn_id === row.dbId).map((transaction) => (
                        <tr key={transaction.id || transaction.transaction_id}>
                          <td style={{ padding: '4px', border: '1px solid #ccc', fontSize: 'inherit', display: 'table-cell', whiteSpace: 'nowrap' }}>{transaction.adjustment_group}</td>
                          <td style={{ padding: '4px', border: '1px solid #ccc', fontSize: 'inherit', display: 'table-cell', whiteSpace: 'nowrap' }}>{transaction.new_batch_id}</td>
                          <td style={{ padding: '4px', border: '1px solid #ccc', fontSize: 'inherit', display: 'table-cell', whiteSpace: 'nowrap' }}>{transaction.transaction_id}</td>
                          <td style={{ padding: '4px', border: '1px solid #ccc', fontSize: 'inherit', display: 'table-cell', whiteSpace: 'nowrap' }}>{formatNumber(transaction.original_quantity)} GAL</td>
                          <td style={{ padding: '4px', border: '1px solid #ccc', fontSize: 'inherit', display: 'table-cell', whiteSpace: 'nowrap' }}>{formatNumber(transaction.quantity)} GAL</td>
                          <td style={{ padding: '4px', border: '1px solid #ccc', fontSize: 'inherit', display: 'table-cell', whiteSpace: 'nowrap' }}>{formatNumber(transaction.final_quantity)} GAL</td>
                          <td style={{ padding: '4px', border: '1px solid #ccc', fontSize: 'inherit', display: 'table-cell', whiteSpace: 'nowrap' }}>${threeDecimalformatCurrency(Number(transaction.original_transaction_value))}</td>
                          <td style={{ padding: '4px', border: '1px solid #ccc', fontSize: 'inherit', display: 'table-cell', whiteSpace: 'nowrap' }}>${threeDecimalformatCurrency(Number(transaction.final_transaction_value))}</td>
                          <td style={{ padding: '4px', border: '1px solid #ccc', fontSize: 'inherit', display: 'table-cell', whiteSpace: 'nowrap' }}>{formatDate(transaction.date_created)}</td>
                          <td style={{ padding: '4px', border: '1px solid #ccc', fontSize: 'inherit', display: 'table-cell', textAlign: 'center' }}>
                            <IconButton 
                              size="small" 
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteAdjustment(transaction);
                              }}
                              color="error"
                              sx={{ padding: '2px' }}
                            >
                              <DeleteForeverIcon fontSize="small" sx={{ color: '#d32f2f' }} />
                            </IconButton>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {adjustmentTransactions.filter(t => t.new_batch_id === row.batch_id && t.transaction_id === row.transaction_id && t.original_quantity === row.quantity && t.trn_rltn_id === row.dbId).forEach((transaction) => {
                    adjustedQuantity += parseFloat(transaction.quantity);
                    numberOfAdjustments++;
                  })}
                  {numberOfAdjustments > 1 ?
                  (
                    <table style={{ borderCollapse: 'separate', borderSpacing: 0, marginTop: '8px', tableLayout: 'auto', width: '100%', borderRadius: '8px', overflow: 'hidden', border: '1px solid #ccc' }}>
                      <colgroup>
                        <col style={{ width: '1%' }} />
                        <col style={{ width: '1%' }} />
                      </colgroup>
                      <thead>
                        <tr>
                          <th style={{ padding: '4px', border: '1px solid #ccc', fontWeight: 'bold', fontSize: 'inherit', display: 'table-cell', whiteSpace: 'nowrap', backgroundColor: '#edca33' }}>{t('FINAL_TRANSACTION_QUANTITY')}</th>
                          <th style={{ padding: '4px', border: '1px solid #ccc', fontWeight: 'bold', fontSize: 'inherit', display: 'table-cell', whiteSpace: 'nowrap', backgroundColor: '#edca33' }}>{t('FINAL_TRANSACTION_VALUE')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr key={subTableRowId++}>
                          <td style={{ padding: '4px', border: '1px solid #ccc', fontSize: 'inherit', display: 'table-cell', whiteSpace: 'nowrap' }}>{formatNumber(row.quantity - adjustedQuantity)} GAL</td>
                          <td style={{ padding: '4px', border: '1px solid #ccc', fontSize: 'inherit', display: 'table-cell', whiteSpace: 'nowrap' }}>${threeDecimalformatCurrency(Number(row.price_per_unit * (row.quantity - adjustedQuantity)))}</td>
                        </tr>
                      </tbody>
                    </table>
                  ):null}
                </div>
              </Collapse>
            )}
            {/* hasta este tag 001 */}
          </TableCell>
        </TableRow>
      </Fragment>
    );
  }

  return (
    <div className="p-8">
      <div className='flex justify-between items-center mb-4'>
        <h1 className="text-2xl font-bold">{t('FUEL_AND_LUBE_TRANSACTIONS')}</h1>
        <Button
          variant="contained"
          color="primary"
          onClick={handleOpenAddTransaction}
        >
          <AddCircleIcon style={{ marginRight: '8px' }} />
          {t('ADD_TRANSACTION')}
        </Button>
      </div>
      <ModalAddFuelTransaction open={openAddTransaction} setOpen={setOpenAddTransaction} onAdded={getFullData} tankData={fuelTanks} driverData={driverData} truckData={truckData} _fuelnLubeCatalog={fuelnLubeCatalog} />
      <ModalEditTransaction 
        open={openEditTransaction} 
        setOpen={setOpenEditTransaction} 
        onUpdate={handleUpdateTransaction}
        selectedTransaction={selectedTransaction}
        driverData={driverData}
        truckData={truckData}
        compacctnum={compacctnum}
      />
        {/* Unified Filter Controls */}
        <div className='flex justify-center items-center mb-6' style={{ gap: '16px' }}>
          <Button
            variant="contained"
            color="primary"
            onClick={applyAllFilters}
            disabled={loading}
            style={{ minWidth: '150px' }}
          >
            {t('APPLY_FILTERS')}
          </Button>
          <Button
            variant="outlined"
            color="secondary"
            onClick={clearAllFilters}
            disabled={loading}
            style={{ minWidth: '150px' }}
          >
            {t('CLEAR_ALL_FILTERS')}
          </Button>
        </div>
        
        <div className='flex flex-1 justify-between items-center' style={{ marginBottom: '4px', gap: '24px', alignItems: 'end' }}>
          <div className="flex-1">
            <h2 className="text-xl font-bold mb-4" style={{ margin: 0 }}>{t('FILTER_BY_BATCH')}</h2>
            {/* Autocomplete to filter by Batch # */}
            <Autocomplete
              options={[...new Set(fuelBatches.map(t => t.batch_id))]}
              getOptionLabel={(option) => option ? option.toString() : ''}
              value={selectedBatch}
              onChange={(event, value) => {
                if (value) {
                  // Validar que el batch seleccionado tenga datos válidos
                  let selectedBatchData = fuelBatches.find(t => t.batch_id === value);
                  if (!selectedBatchData) {
                    // Try comparing as strings
                    selectedBatchData = fuelBatches.find(t => String(t.batch_id) === String(value));
                  }
                  
                  if (!selectedBatchData || (!selectedBatchData.quantity && !selectedBatchData.fuel_qty) || (!selectedBatchData.price_per_unit && !selectedBatchData.price_per_gallon)) {
                    showMessage(t('SELECTED_BATCH_INCOMPLETE'), 'warning');
                    return;
                  }
                  
                  setSelectedBatch(value);
                  setFilterByBatch(true);
                  setSelectedBatchData(selectedBatchData);
                } else {
                  setSelectedBatch(null);
                  setFilterByBatch(false);
                  setSelectedBatchData(null);
                }
              }}
              renderInput={(params) => (
                <TextField {...params} label={t('BATCH_NUMBER')} variant="outlined" />
              )}
              style={{ width: '100%', maxWidth: 300, marginBottom: 16 }}
            />
          </div>
          <div className="flex-1">
            <h2 className="text-xl font-bold mb-4" style={{ margin: 0 }}>{t('FILTER_BY_TRUCK')}</h2>
            {/* Autocomplete to filter by Truck */}
            <Autocomplete
              options={currentTrucksWhitTransactions}
              getOptionLabel={(option) => option ? `No. ${option.t_no_truck}` : ''}
              value={selectedTruck}
              onChange={(event, value) => {
                if (value) {
                  const truckToFilter = currentTrucksWhitTransactions.find(t => t.t_no_truck === value.t_no_truck);
                  setSelectedTruck(truckToFilter);
                  setFilterByTruck(true);
                } else {
                  setSelectedTruck(null);
                  setFilterByTruck(false);
                }
              }}
              renderInput={(params) => (
                <TextField {...params} label={t('TRUCK_NUMBER')} variant="outlined" />
              )}
              style={{ width: '100%', maxWidth: 300, marginBottom: 16 }}
            />
          </div>
          <div className="flex-1">
            <h2 className="text-xl font-bold mb-4" style={{ margin: 0 }}>{t('FILTER_BY_DRIVERS')}</h2>
            {/* Autocomplete to filter by Drivers */}
            <Autocomplete
              multiple
              options={currentDriversWhitTransactions}
              getOptionLabel={(option) => option ? option.name : ''}
              value={selectedDrivers}
              onChange={(event, value) => {
                setSelectedDrivers(value || []);
                setFilterByDriver(value && value.length > 0);
              }}
              renderInput={(params) => (
                <TextField {...params} label={t('ALL_DRIVERS')} variant="outlined" />
              )}
              style={{ width: '100%', maxWidth: 300, marginBottom: 16 }}
            />
          </div>
          <div className="flex-1">
            <h2 className="text-xl font-bold mb-4" style={{ margin: 0 }}>{t('FILTER_BY_DATE_RANGE')}</h2>
            <div style={{ display: 'flex', gap: '16px', marginBottom: '16px' }}>
              <TextField
                type="date"
                value={startDate ? startDate.toISOString().split('T')[0] : ''}
                onChange={(e) => {
                  const dateValue = e.target.value ? new Date(e.target.value) : null;
                  setStartDate(dateValue);
                  setFilterByDateRange(dateValue && endDate);
                }}
                InputLabelProps={{
                  shrink: true,
                }}
                inputProps={{sx:{paddingTop:'7.5px !important', paddingBottom:'7.5px !important'}}}
                variant="outlined"
                style={{ flex: 1 }}
              />
              <TextField
                type="date"
                value={endDate ? endDate.toISOString().split('T')[0] : ''}
                onChange={(e) => {
                  const dateValue = e.target.value ? new Date(e.target.value) : null;
                  setEndDate(dateValue);
                  setFilterByDateRange(startDate && dateValue);
                }}
                InputLabelProps={{
                  shrink: true,
                }}
                inputProps={{sx:{paddingTop:'7.5px !important', paddingBottom:'7.5px !important'}}}
                variant="outlined"
                style={{ flex: 1 }}
              />
            </div>
          </div>
          <div className="flex-1">
            <h2 className="text-xl font-bold mb-4" style={{ margin: 0 }}>
              {loading ? '' : (pastBatches ? t('PAST_BATCHES') : t('ACTIVE_BATCHES'))}
            </h2>
            <Switch
              checked={pastBatches}
              onChange={() => { 
                setPastBatches(!pastBatches); 
                BatchesSelector(pastBatches); 
              }}
              color="primary"
            />
          </div>
          {selectedBatch && (
            <>
              <div>
                <h2 style={{ marginTop: '32px', marginLeft: '38px' }} className="text-xl font-bold mb-4">Fuel/Lube Value</h2>
                <p style={{ marginLeft: '38px' }}>${threeDecimalformatCurrency(parseFloat(parseFloat(selectedBatchData.quantity || selectedBatchData.fuel_qty) * parseFloat(selectedBatchData.price_per_unit || selectedBatchData.price_per_gallon)))}</p>
                <h2 style={{ marginTop: '5px', marginLeft: '38px' }} className="text-xl font-bold mb-4">Remaining Fuel/Lube Value</h2>
                <p style={{ marginLeft: '38px' }}>${threeDecimalformatCurrency(parseFloat((parseFloat(selectedBatchData.quantity || selectedBatchData.fuel_qty) - transactions.filter(t => String(t.batch_id) === String(selectedBatch)).reduce((acc, t) => acc + parseFloat(t.quantity), 0)) * parseFloat(selectedBatchData.price_per_unit || selectedBatchData.price_per_gallon)))}</p>
              </div>
              <div>
                <h2 style={{ marginTop: '32px', marginLeft: '38px' }} className="text-xl font-bold mb-4">Fuel/Lube QTY</h2>
                <p style={{ marginLeft: '38px' }}>{formatNumber(selectedBatchData.quantity || selectedBatchData.fuel_qty)} Units</p>
                <h2 style={{ marginTop: '5px', marginLeft: '38px' }} className="text-xl font-bold mb-4">Remaining Fuel/Lube QTY</h2>
                <p style={{ marginLeft: '38px' }}>{formatNumber(parseFloat((parseFloat(selectedBatchData.quantity || selectedBatchData.fuel_qty) - transactions.filter(t => String(t.batch_id) === String(selectedBatch)).reduce((acc, t) => acc + parseFloat(t.quantity), 0))).toFixed(3))} Units</p>
              </div>
              <div>
                <h2 style={{ marginTop: '32px', marginLeft: '38px' }} className="text-xl font-bold mb-4">Fuel/Lube Taken</h2>
                <p style={{ marginLeft: '38px' }}>{formatNumber(parseFloat(transactions.filter(t => String(t.batch_id) === String(selectedBatch)).reduce((acc, t) => acc + parseFloat(t.quantity), 0)).toFixed(3))} Units</p>
                <h2 style={{ marginTop: '5px', marginLeft: '38px' }} className="text-xl font-bold mb-4">Fuel/Lube Taken Value</h2>
                <p style={{ marginLeft: '38px' }}>
                  ${threeDecimalformatCurrency(
                    parseFloat(parseFloat(selectedBatchData.quantity || selectedBatchData.fuel_qty) * parseFloat(selectedBatchData.price_per_unit || selectedBatchData.price_per_gallon)) - 
                    parseFloat((parseFloat(selectedBatchData.quantity || selectedBatchData.fuel_qty) - transactions.filter(t => String(t.batch_id) === String(selectedBatch)).reduce((acc, t) => acc + parseFloat(t.quantity), 0)) * parseFloat(selectedBatchData.price_per_unit || selectedBatchData.price_per_gallon))
                  )}
                </p>
              </div>
            </>
          )}
        </div>
      <div style={{ 
        width: '100%', 
        height: 'calc(100vh - 400px)', 
        minHeight: '400px',
        overflow: 'auto'
      }} className="custom-datagrid-header">
        {loading ? (
          <FuseLoading />
        ) : transactions.length === 0 ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
            <Typography variant="h6" color="textSecondary">
              No transactions found
            </Typography>
          </Box>
        ) : (
          <TableContainer 
            component={Paper} 
            className='responsive-loads-table'
            sx={{ 
              maxHeight: '100%',
              width: '100%',
              overflow: 'auto'
            }}
          >
            <Table aria-label='collapsible table' stickyHeader>
              <TableHead sx={{ position: 'sticky', top: 0, zIndex: 10 }}>
                <TableRow style={{ backgroundColor: '#edca33' }}>
                  <TableCell className='font-bold' sx={{ backgroundColor: '#edca33' }}></TableCell>
                  <TableCell 
                    className='font-bold'
                    sx={{ backgroundColor: '#edca33' }}
                    sortDirection={orderBy === 'batch_id' ? order : false}
                  >
                    <TableSortLabel
                      active={orderBy === 'batch_id'}
                      direction={orderBy === 'batch_id' ? order : 'asc'}
                      onClick={(e) => handleRequestSort(e, 'batch_id')}
                    >
                      {t('BATCH_NUMBER')}
                      {orderBy === 'batch_id' ? (
                        <Box component="span" sx={visuallyHidden}>
                          {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                        </Box>
                      ) : null}
                    </TableSortLabel>
                  </TableCell>
                  <TableCell 
                    className='font-bold'
                    sx={{ backgroundColor: '#edca33' }}
                    sortDirection={orderBy === 'transaction_id' ? order : false}
                  >
                    <TableSortLabel
                      active={orderBy === 'transaction_id'}
                      direction={orderBy === 'transaction_id' ? order : 'asc'}
                      onClick={(e) => handleRequestSort(e, 'transaction_id')}
                    >
                      {t('TRANSACTION_ID')}
                      {orderBy === 'transaction_id' ? (
                        <Box component="span" sx={visuallyHidden}>
                          {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                        </Box>
                      ) : null}
                    </TableSortLabel>
                  </TableCell>
                  <TableCell 
                    className='font-bold'
                    sx={{ backgroundColor: '#edca33' }}
                    sortDirection={orderBy === 'date_created' ? order : false}
                  >
                    <TableSortLabel
                      active={orderBy === 'date_created'}
                      direction={orderBy === 'date_created' ? order : 'asc'}
                      onClick={(e) => handleRequestSort(e, 'date_created')}
                    >
                      {t('DATE')}
                      {orderBy === 'date_created' ? (
                        <Box component="span" sx={visuallyHidden}>
                          {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                        </Box>
                      ) : null}
                    </TableSortLabel>
                  </TableCell>
                  <TableCell 
                    className='font-bold'
                    sx={{ backgroundColor: '#edca33' }}
                    sortDirection={orderBy === 'quantity' ? order : false}
                  >
                    <TableSortLabel
                      active={orderBy === 'quantity'}
                      direction={orderBy === 'quantity' ? order : 'asc'}
                      onClick={(e) => handleRequestSort(e, 'quantity')}
                    >
                      {t('FUEL_LUBE_QUANTITY')}
                      {orderBy === 'quantity' ? (
                        <Box component="span" sx={visuallyHidden}>
                          {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                        </Box>
                      ) : null}
                    </TableSortLabel>
                  </TableCell>
                  <TableCell 
                    className='font-bold'
                    sx={{ backgroundColor: '#edca33' }}
                    sortDirection={orderBy === 'price_per_unit' ? order : false}
                  >
                    <TableSortLabel
                      active={orderBy === 'price_per_unit'}
                      direction={orderBy === 'price_per_unit' ? order : 'asc'}
                      onClick={(e) => handleRequestSort(e, 'price_per_unit')}
                    >
                      {t('PRICE_PER_UNIT')}
                      {orderBy === 'price_per_unit' ? (
                        <Box component="span" sx={visuallyHidden}>
                          {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                        </Box>
                      ) : null}
                    </TableSortLabel>
                  </TableCell>
                  <TableCell 
                    className='font-bold'
                    sx={{ backgroundColor: '#edca33' }}
                    sortDirection={orderBy === 'value' ? order : false}
                  >
                    <TableSortLabel
                      active={orderBy === 'value'}
                      direction={orderBy === 'value' ? order : 'asc'}
                      onClick={(e) => handleRequestSort(e, 'value')}
                    >
                      {t('TRANSACTION_VALUE')}
                      {orderBy === 'value' ? (
                        <Box component="span" sx={visuallyHidden}>
                          {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                        </Box>
                      ) : null}
                    </TableSortLabel>
                  </TableCell>
                  <TableCell 
                    className='font-bold'
                    sx={{ backgroundColor: '#edca33' }}
                    sortDirection={orderBy === 'driver' ? order : false}
                  >
                    <TableSortLabel
                      active={orderBy === 'driver'}
                      direction={orderBy === 'driver' ? order : 'asc'}
                      onClick={(e) => handleRequestSort(e, 'driver')}
                    >
                      {t('DRIVER')}
                      {orderBy === 'driver' ? (
                        <Box component="span" sx={visuallyHidden}>
                          {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                        </Box>
                      ) : null}
                    </TableSortLabel>
                  </TableCell>
                  <TableCell 
                    className='font-bold'
                    sx={{ backgroundColor: '#edca33' }}
                    sortDirection={orderBy === 'truck' ? order : false}
                  >
                    <TableSortLabel
                      active={orderBy === 'truck'}
                      direction={orderBy === 'truck' ? order : 'asc'}
                      onClick={(e) => handleRequestSort(e, 'truck')}
                    >
                      {t('TRUCK')}
                      {orderBy === 'truck' ? (
                        <Box component="span" sx={visuallyHidden}>
                          {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                        </Box>
                      ) : null}
                    </TableSortLabel>
                  </TableCell>
                  <TableCell 
                    className='font-bold'
                    sx={{ backgroundColor: '#edca33' }}
                    sortDirection={orderBy === 'mileage' ? order : false}
                  >
                    <TableSortLabel
                      active={orderBy === 'mileage'}
                      direction={orderBy === 'mileage' ? order : 'asc'}
                      onClick={(e) => handleRequestSort(e, 'mileage')}
                    >
                      {t('MILEAGE')}
                      {orderBy === 'mileage' ? (
                        <Box component="span" sx={visuallyHidden}>
                          {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                        </Box>
                      ) : null}
                    </TableSortLabel>
                  </TableCell>
                  <TableCell 
                    className='font-bold'
                    sx={{ backgroundColor: '#edca33' }}
                    sortDirection={orderBy === 'tankLocation' ? order : false}
                  >
                    <TableSortLabel
                      active={orderBy === 'tankLocation'}
                      direction={orderBy === 'tankLocation' ? order : 'asc'}
                      onClick={(e) => handleRequestSort(e, 'tankLocation')}
                    >
                      {t('TANK_DESCRIPTION')}
                      {orderBy === 'tankLocation' ? (
                        <Box component="span" sx={visuallyHidden}>
                          {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                        </Box>
                      ) : null}
                    </TableSortLabel>
                  </TableCell>
                  <TableCell 
                    className='font-bold'
                    sx={{ backgroundColor: '#edca33' }}
                    sortDirection={orderBy === 'notes' ? order : false}
                  >
                    <TableSortLabel
                      active={orderBy === 'notes'}
                      direction={orderBy === 'notes' ? order : 'asc'}
                      onClick={(e) => handleRequestSort(e, 'notes')}
                    >
                      {t('NOTES')}
                      {orderBy === 'notes' ? (
                        <Box component="span" sx={visuallyHidden}>
                          {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                        </Box>
                      ) : null}
                    </TableSortLabel>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(rowsPerPage > 0
                  ? (orderBy ? stableSort(transactions, getComparator(order, orderBy)) : transactions).slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  : (orderBy ? stableSort(transactions, getComparator(order, orderBy)) : transactions)
                ).map((row) => (
                  <TransactionRow key={row.dbId} row={row} />
                ))}
              </TableBody>
              <TableFooter>
                <TableRow>
                  <TablePagination
                    rowsPerPageOptions={[10, 25, 50, 100, { label: 'All', value: -1 }]}
                    colSpan={12}
                    count={transactions.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    SelectProps={{
                      inputProps: {
                        'aria-label': 'rows per page',
                      },
                      native: true,
                    }}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    ActionsComponent={TablePaginationActions}
                  />
                </TableRow>
              </TableFooter>
            </Table>
          </TableContainer>
        )}
      </div>
      
      {/* Delete Adjustment Response Modal */}
      <Dialog
        open={openDeleteAdjustmentModal}
        onClose={handleCloseDeleteAdjustmentModal}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Typography variant="h6" component="div">
            {deleteAdjustmentResponse?.error ? t('ERROR') || 'Error' : t('ADJUSTMENT_DELETION_CONFIRMATION') || 'Adjustment Deletion Confirmation'}
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Typography variant="body1">
              <p>
                <b>This are the transactions that will be deleted:</b>
              </p>
              <br />
              {deleteAdjustmentResponse?.data?.adjTransactions?.map((transaction) => (
                <p>
                  <b style={{ fontWeight: 'bold' }}>Transaction ID:</b> {transaction.transaction_id}
                  <br />
                  <b style={{ fontWeight: 'bold' }}>Batch ID:</b> {transaction.new_batch_id}
                  <br />
                  <b style={{ fontWeight: 'bold' }}>Adjustment Quantity:</b> {transaction.quantity}
                  <br />
                  <br />
                </p>
              ))}
              <p>
                <b style={{ fontWeight: 'bold' }}>Total Quantity Adjusted:</b> {deleteAdjustmentResponse?.data?.adjTransactions?.reduce((acc, transaction) => acc + parseFloat(transaction.quantity), 0)}
              </p>
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', mr: 'auto' }}>
            <br />
            <FormControlLabel
              control={
                <Checkbox
                  checked={confirmDeleteCheckbox}
                  onChange={(e) => setConfirmDeleteCheckbox(e.target.checked)}
                  color="error"
                />
              }
              label="The following adjustment transaction will be deleted, and this action cannot be undone."
            />
            <br />
            <FormControlLabel
              control={
                <Checkbox
                  checked={confirmInventoryReductionCheckbox}
                  onChange={(e) => setConfirmInventoryReductionCheckbox(e.target.checked)}
                  color="error"
                />
              }
              label="This means that the inventory amount available will be reduced by the quantity of the adjustment transaction"
            />
            <br />
          </Box>
          <Button onClick={handleCloseDeleteAdjustmentModal} color="secondary">
            {t('CANCEL') || 'Cancel'}
          </Button>
          <Button 
            onClick={handleProceedDeleteAdjustment} 
            color="error" 
            variant="contained"
            disabled={!confirmDeleteCheckbox || !confirmInventoryReductionCheckbox}
          >
            {t('PROCEED') || 'Proceed'}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default Transactions;
